import React from "react";
import "style/Hiring.scss";

import { useSearchParams } from "react-router-dom";

import hiring, {
  CONTACT_MAIL,
  HIRING_LANGS,
  HiringLang,
} from "config/i18n/hiring";
import useLang from "hooks/useLang";
import usePageTitle from "hooks/usePageTitle";

import Reveal from "components/Reveal";

const isHiringLang = (value: string | null): value is HiringLang =>
  HIRING_LANGS.some((item) => item.code === value);

/**
 * "We are hiring" – the one page that exists in more languages than the
 * site itself (EN/DE/FR/ES/ZH). The language is page-local: `?lang=fr`
 * wins (shareable link for job boards), otherwise the site language.
 */
const Hiring = () => {
  const siteLang = useLang();
  // The URL is the single source of truth, so Back/Forward between
  // differently parametrised links keeps page and URL in sync.
  const [params, setParams] = useSearchParams();
  const param = params.get("lang");
  // The posting exists in its own set of languages; others fall back to EN.
  const pageLang: HiringLang = isHiringLang(param)
    ? param
    : isHiringLang(siteLang)
    ? siteLang
    : "en";

  const t = hiring[pageLang];
  // lang goes on the page container only – nav and footer stay in the
  // site language, and the html element keeps its own attribute.
  const htmlLang =
    HIRING_LANGS.find((item) => item.code === pageLang)?.htmlLang || "en";
  usePageTitle(t.pageTitle);

  const switchLang = (code: HiringLang) =>
    setParams({ lang: code }, { replace: true });

  const mailto = `mailto:${CONTACT_MAIL}?subject=${encodeURIComponent(
    t.apply.mailSubject,
  )}`;

  return (
    <div className="lp lp--hiring" lang={htmlLang}>
      <header className="lp-hero">
        <div className="lp-hero__grid" aria-hidden="true" />
        <div className="lp-hero__overlay" aria-hidden="true" />

        <div className="shell lp-hero__inner">
          <div className="lp-hero__copy">
            {/* The page name stays English in every language. */}
            <span className="lp-hero__eyebrow" lang="en">
              {t.eyebrow}
            </span>
            <h1 className="lp-hero__title">{t.title}</h1>

            <ul className="lp-chips hiring__facts">
              {t.facts.map((fact) => (
                <li key={fact} className="chip">
                  {fact}
                </li>
              ))}
            </ul>

            <div
              className="hiring__langs"
              role="group"
              aria-label={t.langLabel}
            >
              {HIRING_LANGS.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  lang={item.htmlLang}
                  className={item.code === pageLang ? "is-active" : ""}
                  aria-pressed={item.code === pageLang}
                  onClick={() => switchLang(item.code)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-section lp-section--light">
          <div className="shell hiring__layout">
            <article className="hiring__body">
              {t.intro.map((paragraph) => (
                <p key={paragraph} className="hiring__lead">
                  {paragraph}
                </p>
              ))}

              <Reveal className="hiring__callout">
                <h2>{t.challenge.title}</h2>
                <p>{t.challenge.text}</p>
              </Reveal>

              <h2 className="hiring__h2">{t.deliverables.title}</h2>
              <ul className="lp-list">
                {t.deliverables.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>

            <aside
              className="hiring__aside"
              aria-labelledby="hiring-card-title"
            >
              <div className="hiring__card">
                <span className="lp-card__kicker" lang="en">
                  {t.eyebrow}
                </span>
                <h2 id="hiring-card-title">{t.title}</h2>
                <ul className="hiring__card-facts">
                  {t.facts.map((fact) => (
                    <li key={fact}>{fact}</li>
                  ))}
                </ul>
                <a className="btn btn--primary btn--block" href={mailto}>
                  {t.apply.button}
                </a>
                <a className="hiring__mail" href={mailto}>
                  {CONTACT_MAIL}
                </a>
              </div>
            </aside>
          </div>
        </section>

        <section className="lp-section lp-section--muted">
          <div className="shell">
            <Reveal className="lp-cta">
              <div>
                <h2>{t.apply.title}</h2>
                <p>{t.apply.text}</p>
              </div>
              <div className="lp-cta__actions">
                <a className="btn btn--primary" href={mailto}>
                  {t.apply.button}
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Hiring;
